import React, { useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSuccessNotification, setErrorNotification } from '../../../actions/notificationAction';
import { FulfillmentCenterAPI } from '../../../api/fulfillmentCenterApi';
import { ShippingApi } from '../api/shippingApi';
import { useShippingProfile } from '../contexts/ShippingProfileContext';
import EditShippingProfile from '../components/EditShippingProfile';

const EditShippingProfileContainer = () => {
  const { store_id, id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const { shippingProfile, setShippingProfile, shipmentLocation, setShipmentLocation, selectedZones, setSelectedZones, selectedCarriers, setSelectedCarriers, fulfillmentCenters, setFulfillmentCenters, setSaving, } = useShippingProfile();

  const isEditMode = Boolean(id);

  const fetchData = useCallback(async () => {
    try {
      const [fulfillmentCentersResponse, shippingProfileResponse] = await Promise.all([
        FulfillmentCenterAPI.getFulfillmentCenters({ store_id }),
        isEditMode ? ShippingApi.getShippingProfile(store_id, id) : null,
      ]);

      setFulfillmentCenters(fulfillmentCentersResponse.data.data);

      if (isEditMode && shippingProfileResponse) {
        const profileData = shippingProfileResponse.data;
        setShippingProfile({
          id: profileData.id,
          name: profileData.name,
          status: profileData.status,
          fulfillment_center_id: profileData.fulfillment_center_id,
        });
        setShipmentLocation(profileData.location || { name: '', address: '' });
        setSelectedZones(profileData.location?.shippingZones || []);
        setSelectedCarriers(profileData.shippingCarriers || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [isEditMode,store_id,id,setFulfillmentCenters,setShippingProfile,setShipmentLocation,setSelectedZones,setSelectedCarriers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const completeProfileData = {
        ...shippingProfile,
        location: shipmentLocation,
        zones: selectedZones.map((zone) => ({
          id: zone.id,
          name: zone.name,
          regions: zone.regions.map((region) => ({
            id: region.id,
            name: region.name,
            country_code: region.country_code,
            region_code: region.region_code,
            flag: region.flag
          })),
          rates: zone.shippingRates?.map((rate) => ({
            id: rate.id,
            name: rate.name,
            rate_type: rate.rate_type,
            price: rate.price,
            min_price: rate.min_price,
            max_price: rate.max_price,
            description: rate.description,
          })),          
        })),
        carriers: selectedCarriers.map((carrier) => ({
          id: carrier.id,
          name: carrier.name,
          tracking_url: carrier.tracking_url,
          carrier_type: carrier.carrier_type,
          fulfillment_center_id: carrier.fulfillment_center_id,
        })),
      };

      if (isEditMode) {
        await ShippingApi.updateShippingProfile(store_id, shippingProfile.id, completeProfileData);
      } else {
        await ShippingApi.createShippingProfile(store_id, completeProfileData);
      }

      dispatch(
        setSuccessNotification({ message: `Shipping profile ${isEditMode ? 'updated' : 'created'} successfully.` })
      );
      history.push(`/admin/stores/${store_id}/shipping-profiles`);
    } catch (error) {
      dispatch(setErrorNotification({ message: error.response?.data?.error || error.message }));
    } finally {
      setSaving(false);
    }
  };

  return <EditShippingProfile onSave={handleSaveProfile} isEditMode={isEditMode} />;
};

export default EditShippingProfileContainer;